import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    format,
    getDay,
    isSameDay,
    isSameMonth,
    isWithinInterval,
    startOfMonth,
    subMonths,
} from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../common/utils/format-utils.js'
import './portal-date-range-picker.css'

export interface DateRange {
    start: string // YYYY-MM-DD
    end: string // YYYY-MM-DD
}

interface PortalDateRangePickerProps {
    value: DateRange | null
    onChange: (range: DateRange | null) => void
    lang: Language
    minDate?: Date
    /** When true, render a compact trigger field that opens the calendar in a popover. */
    asField?: boolean
    /** Trigger text shown when no range is selected (field mode only). */
    placeholder?: string
}

const monthLabels: Record<Language, string[]> = {
    et: [
        'Jaanuar',
        'Veebruar',
        'Märts',
        'Aprill',
        'Mai',
        'Juuni',
        'Juuli',
        'August',
        'September',
        'Oktoober',
        'November',
        'Detsember',
    ],
    en: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ],
}

const dayLabels: Record<Language, string[]> = {
    et: ['E', 'T', 'K', 'N', 'R', 'L', 'P'],
    en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
}

const toDateStr = (d: Date): string => format(d, 'yyyy-MM-dd')

interface MonthGridProps {
    month: Date
    lang: Language
    isInRange: (day: Date) => boolean
    isStart: (day: Date) => boolean
    isEnd: (day: Date) => boolean
    isDisabled: (day: Date) => boolean
    onDayClick: (day: Date) => void
    onDayHover: (day: Date | null) => void
    pickingEnd: boolean
}

const MonthGrid = ({
    month,
    lang,
    isInRange,
    isStart,
    isEnd,
    isDisabled,
    onDayClick,
    onDayHover,
    pickingEnd,
}: MonthGridProps) => {
    const mStart = startOfMonth(month)
    const mEnd = endOfMonth(month)
    const allDays = eachDayOfInterval({ start: mStart, end: mEnd })
    const startDow = (getDay(mStart) + 6) % 7
    const blanks = Array.from({ length: startDow })

    const isWeekend = (day: Date): boolean => {
        const dow = getDay(day)
        return dow === 0 || dow === 6
    }

    return (
        <div className="portal-drp__month">
            <div className="portal-drp__month-title">
                {monthLabels[lang][month.getMonth()]} {month.getFullYear()}
            </div>
            <div className="portal-drp__weekdays">
                {dayLabels[lang].map((d) => (
                    <div key={d} className="portal-drp__weekday">
                        {d}
                    </div>
                ))}
            </div>
            <div className="portal-drp__grid">
                {blanks.map((_, i) => (
                    <div key={`blank-${i}`} className="portal-drp__cell portal-drp__cell--blank" />
                ))}
                {allDays.map((day) => {
                    const disabled = isDisabled(day)
                    const inRange = isInRange(day)
                    const start = isStart(day)
                    const end = isEnd(day)
                    const weekend = isWeekend(day)
                    const today = isSameDay(day, new Date())
                    const inMonth = isSameMonth(day, month)

                    return (
                        <button
                            key={toDateStr(day)}
                            type="button"
                            className={[
                                'portal-drp__cell',
                                inRange && 'is-in-range',
                                start && 'is-start',
                                end && 'is-end',
                                weekend && 'is-weekend',
                                today && 'is-today',
                                disabled && 'is-disabled',
                                !inMonth && 'is-outside',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            disabled={disabled}
                            onClick={() => onDayClick(day)}
                            onMouseEnter={() => pickingEnd && onDayHover(day)}
                            onMouseLeave={() => onDayHover(null)}
                        >
                            {day.getDate()}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export const PortalDateRangePicker = ({
    value,
    onChange,
    lang,
    minDate,
    asField = false,
    placeholder,
}: PortalDateRangePickerProps) => {
    const [viewMonth, setViewMonth] = useState(
        value?.start ? new Date(value.start + 'T00:00:00') : new Date(),
    )
    const [hoverDate, setHoverDate] = useState<Date | null>(null)
    const [pickingEnd, setPickingEnd] = useState(false)
    const [tempStart, setTempStart] = useState<Date | null>(
        value?.start ? new Date(value.start + 'T00:00:00') : null,
    )
    const [isOpen, setIsOpen] = useState(false)
    // Popover anchor — viewport coords of the trigger field. The popover is
    // rendered through a portal (see below) so it escapes the modal's
    // overflow-clipping and shows both months at full width below the field.
    const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)

    const openPopover = () => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchor({ top: rect.bottom + 6, left: rect.left, width: rect.width })
        // The value often arrives after mount (e.g. the edit modal pre-fills it
        // in an effect), so the mount-time viewMonth may point at today — jump
        // to the selected range every time the popover opens. Also drop any
        // half-finished pick from a previous open, or the first click here
        // would silently complete that stale range.
        setViewMonth(value?.start ? new Date(value.start + 'T00:00:00') : new Date())
        setPickingEnd(false)
        setTempStart(null)
        setIsOpen(true)
    }

    // Close the popover on outside click, Escape, or any scroll/resize (the
    // popover is fixed-positioned, so it would otherwise drift out of alignment).
    useEffect(() => {
        if (!asField || !isOpen) return

        const isInside = (target: Node) =>
            triggerRef.current?.contains(target) || popoverRef.current?.contains(target)

        const handleClick = (e: MouseEvent) => {
            if (!isInside(e.target as Node)) setIsOpen(false)
        }
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false)
        }
        const close = () => setIsOpen(false)

        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleKey)
        window.addEventListener('resize', close)
        // Capture phase catches scrolls on the modal/any ancestor, not just window.
        window.addEventListener('scroll', close, true)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleKey)
            window.removeEventListener('resize', close)
            window.removeEventListener('scroll', close, true)
        }
    }, [asField, isOpen])

    // Committing a pick can mount/grow content above the field (the booking
    // modal's day-count banner), shifting the trigger — re-anchor the popover
    // to the field's new position so it doesn't drift mid-pick.
    useEffect(() => {
        if (!asField || !isOpen) return
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchor({ top: rect.bottom + 6, left: rect.left, width: rect.width })
    }, [asField, isOpen, value])

    const nextMonth = addMonths(viewMonth, 1)

    // The first click commits the day as a single-day range right away; the
    // second click extends it into the full range. Anything that interrupts the
    // pick (Save, outside click, scroll) therefore keeps the clicked day
    // instead of silently reverting to the previous range — with the old
    // arm-then-commit flow, moving a one-day absence and hitting Salvesta
    // submitted the unchanged dates as a no-op (TASK-402 follow-up).
    const handleDayClick = (day: Date) => {
        if (!pickingEnd || !tempStart) {
            setTempStart(day)
            setPickingEnd(true)
            onChange({ start: toDateStr(day), end: toDateStr(day) })
        } else {
            const start = day < tempStart ? day : tempStart
            const end = day < tempStart ? tempStart : day
            onChange({ start: toDateStr(start), end: toDateStr(end) })
            setPickingEnd(false)
            setTempStart(null)
            if (asField) setIsOpen(false)
        }
    }

    // While a new pick is in progress it owns the highlight — the retained
    // committed value would otherwise keep painting the old range under it.
    const isInRange = (day: Date): boolean => {
        if (pickingEnd && tempStart) {
            // No hover yet → the committed single day is the whole selection.
            const target = hoverDate ?? tempStart
            const s = target < tempStart ? target : tempStart
            const e = target < tempStart ? tempStart : target
            return isWithinInterval(day, { start: s, end: e })
        }
        if (value?.start && value?.end) {
            const s = new Date(value.start + 'T00:00:00')
            const e = new Date(value.end + 'T00:00:00')
            return isWithinInterval(day, { start: s, end: e })
        }
        return false
    }

    const isStartDay = (day: Date): boolean => {
        if (pickingEnd && tempStart) return isSameDay(day, tempStart)
        if (value?.start) return isSameDay(day, new Date(value.start + 'T00:00:00'))
        return false
    }

    const isEndDay = (day: Date): boolean => {
        if (pickingEnd && tempStart) {
            const target = hoverDate ?? tempStart
            const end = target < tempStart ? tempStart : target
            return isSameDay(day, end)
        }
        if (value?.end) return isSameDay(day, new Date(value.end + 'T00:00:00'))
        return false
    }

    const isDisabled = (day: Date): boolean => {
        if (minDate && day < minDate) return true
        return false
    }

    const sharedProps = {
        lang,
        isInRange,
        isStart: isStartDay,
        isEnd: isEndDay,
        isDisabled,
        onDayClick: handleDayClick,
        onDayHover: setHoverDate,
        pickingEnd,
    }

    const grid = (
        <>
            {/* Navigation arrows */}
            <div className="portal-drp__header">
                <button
                    type="button"
                    className="portal-drp__nav-btn"
                    onClick={() => setViewMonth(subMonths(viewMonth, 1))}
                    aria-label={t.common.previousMonth(lang)}
                >
                    ←
                </button>
                <button
                    type="button"
                    className="portal-drp__nav-btn"
                    onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                    aria-label={t.common.nextMonth(lang)}
                >
                    →
                </button>
            </div>

            {/* Two months side by side */}
            <div className="portal-drp__months">
                <MonthGrid month={viewMonth} {...sharedProps} />
                <MonthGrid month={nextMonth} {...sharedProps} />
            </div>

            {/* Selected range display — hidden mid-pick, when it would show the
                old range the new selection is about to replace. */}
            {!pickingEnd && value?.start && value?.end && (
                <div className="portal-drp__summary">
                    {formatDisplayDate(value.start)} → {formatDisplayDate(value.end)}
                </div>
            )}
            {pickingEnd && <div className="portal-drp__hint">{t.common.selectEndDate(lang)}</div>}
        </>
    )

    if (!asField) {
        return <div className="portal-drp">{grid}</div>
    }

    const hasValue = Boolean(value?.start && value?.end)
    const triggerLabel = hasValue
        ? value!.start === value!.end
            ? formatDisplayDate(value!.start)
            : `${formatDisplayDate(value!.start)} → ${formatDisplayDate(value!.end)}`
        : (placeholder ?? '')

    return (
        <div className="portal-drp__field-wrap">
            <button
                ref={triggerRef}
                type="button"
                className={`portal-drp__field${hasValue ? '' : ' portal-drp__field--placeholder'}`}
                onClick={() => (isOpen ? setIsOpen(false) : openPopover())}
            >
                {triggerLabel}
            </button>
            {isOpen &&
                anchor &&
                createPortal(
                    <div
                        ref={popoverRef}
                        className="portal-drp__popover portal-drp"
                        style={{ top: anchor.top, left: anchor.left, minWidth: anchor.width }}
                    >
                        {grid}
                    </div>,
                    document.body,
                )}
        </div>
    )
}
